<template>
  <div class="admin-user-detail-page">
    <div v-if="isAuthLoading" class="state-block" data-variant="auth">
      <v-progress-circular
        indeterminate
        color="#EA5545"
        size="64"
      />
      <p>{{ t('adminUserDetail.auth.checking') }}</p>
    </div>

    <div v-else-if="needsLogin" class="state-block" data-variant="auth" data-state="empty">
      <v-icon size="64" color="#EA5545">mdi-account-lock</v-icon>
      <h2>{{ t('adminUserDetail.auth.loginTitle') }}</h2>
      <p>{{ t('adminUserDetail.auth.loginDescription') }}</p>
      <router-link to="/login">
        <v-btn
          color="#EA5545"
          variant="flat"
          prepend-icon="mdi-login"
        >
          {{ t('adminUserDetail.auth.loginButton') }}
        </v-btn>
      </router-link>
    </div>

    <div v-else-if="!isAuthorized" class="state-block" data-state="empty" data-variant="auth">
      <v-icon size="64" color="#EA5545">mdi-lock-alert</v-icon>
      <h2>{{ t('adminUserDetail.auth.noAccessTitle') }}</h2>
      <p>{{ authError || t('adminUserDetail.auth.noAccessDescription') }}</p>
    </div>

    <template v-else>
    <div class="page-header">
      <div>
        <p class="eyebrow">{{ t('adminUserDetail.eyebrow') }}</p>
        <h1 class="page-title">
          {{ detail?.user_name || t('adminUserDetail.fallbackTitle') }}
        </h1>
        <p class="page-subtitle">
          {{ t('adminUserDetail.subtitle') }}
        </p>
      </div>
      <div class="header-actions">
        <v-btn
          variant="text"
          color="#3D3D3D"
          @click="goBack"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          {{ t('adminUserDetail.actions.back') }}
        </v-btn>
      </div>
    </div>

    <div
      v-if="detail"
      class="user-summary-card"
    >
      <div class="user-summary-id">#{{ detail.user_id }}</div>
      <h2 class="user-summary-name">
        {{ userSummaryName }}
      </h2>
      <p
        v-if="userSummaryCompany"
        class="user-summary-meta"
      >
        {{ userSummaryCompany }}
      </p>
      <p
        v-if="userSummaryAddress"
        class="user-summary-meta"
      >
        {{ userSummaryAddress }}
      </p>
      <div
        v-if="primaryContractId || primaryContractNumber"
        class="user-summary-contract"
      >
        <span v-if="primaryContractId">Vertrag-ID #{{ primaryContractId }}</span>
        <span v-if="primaryContractNumber">· {{ t('adminSupportIndex.labels.contractNumber', { number: primaryContractNumber }) }}</span>
      </div>
      <p
        v-if="detail.user_company_kundennummer"
        class="user-summary-meta"
      >
        Kundennummer {{ detail.user_company_kundennummer }}
      </p>
    </div>

    <v-progress-linear
      v-if="isLoading"
      color="#EA5545"
      indeterminate
      class="mb-4"
    />

    <v-alert
      v-if="detail && Number(detail.user_status) === 0"
      type="warning"
      variant="tonal"
      class="mb-6"
    >
      {{ t('adminUserDetail.status.pendingNotice') }}
    </v-alert>

    <v-card class="detail-tabs-card">
      <v-tabs
        v-model="activeTab"
        color="#EA5545"
        bg-color="transparent"
      >
        <v-tab value="supports">{{ t('adminUserDetail.tabs.supports') }}</v-tab>
        <v-tab value="profile">{{ t('adminUserDetail.tabs.profile') }}</v-tab>
      </v-tabs>
      <v-divider class="mb-4" />
      <div v-show="activeTab === 'supports'" class="supports-tab">
        <div class="supports-header">
          <div>
            <h2 class="card-title">
              <v-icon size="20" color="#EA5545" class="mr-2">mdi-lifebuoy</v-icon>
              {{ t('adminUserDetail.sections.supports.title') }}
            </h2>
            <p class="card-subtitle">
              {{ t('adminUserDetail.sections.supports.subtitle') }}
            </p>
          </div>
        </div>

        <div
          v-if="supports.length === 0"
          class="state-block support-empty" data-state="empty"
        >
          <v-icon size="40" color="#cbd5f5" class="mb-2">mdi-ticket-outline</v-icon>
          <h4>{{ t('adminUserDetail.sections.supports.emptyTitle') }}</h4>
          <p>{{ t('adminUserDetail.sections.supports.emptyDescription') }}</p>
        </div>

        <div v-else class="support-list">
          <v-card
            v-for="support in supports"
            :key="support.sup_id"
            class="support-card"
            elevation="0"
          >
            <v-card-text>
              <div class="support-card-header">
                <span class="support-id">#{{ support.sup_id }}</span>
                <v-chip
                  :color="getSupportStatusColor(support.sup_status)"
                  variant="tonal"
                  size="small"
                >
                  {{ getSupportStatusLabel(support.sup_status) }}
                </v-chip>
              </div>
              <div class="support-meta">
                <div
                  v-if="supportProductLabel(support)"
                  class="support-meta-item"
                >
                  <v-icon size="16" color="#EA5545" class="mr-1">mdi-package-variant</v-icon>
                  <span>{{ supportProductLabel(support) }}</span>
                </div>
                <div class="support-meta-item">
                  <v-icon size="16" color="#6b7280" class="mr-1">mdi-clock-outline</v-icon>
                  <span>{{ formatSupportDate(support.sup_created_date) }}</span>
                </div>
              </div>
              <div class="support-preview">
                <p class="support-title">
                  {{ support.sup_title || t('adminUserDetail.sections.supports.untitled') }}
                </p>
                <p class="support-text">
                  {{ truncateText(support.sup_text || support.sup_description || '') }}
                </p>
              </div>
              <div class="support-actions">
                <v-btn
                  variant="text"
                  color="#EA5545"
                  @click="goToSupport(support.sup_id)"
                >
                  <v-icon left size="16">mdi-open-in-new</v-icon>
                  {{ t('adminUserDetail.actions.viewSupport') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
      <div v-show="activeTab === 'profile'">
        <v-row>
      <v-col cols="12" md="7">
        <v-card class="mb-6">
          <v-card-title class="card-title">
            <v-icon size="20" color="#EA5545" class="mr-2">mdi-account-circle</v-icon>
            {{ t('adminUserDetail.sections.profile.title') }}
          </v-card-title>
          <v-card-subtitle class="card-subtitle">
            {{ t('adminUserDetail.sections.profile.subtitle') }}
          </v-card-subtitle>
          <v-card-text>
            <v-form @submit.prevent="saveUserChanges">
              <v-text-field
                v-model="form.user_name"
                :label="t('adminUserDetail.fields.name')"
                density="comfortable"
                variant="outlined"
                class="mb-3"
              />
              <v-text-field
                v-model="form.user_vorname"
                :label="t('adminUserDetail.fields.firstName')"
                density="comfortable"
                variant="outlined"
                class="mb-3"
              />
              <v-text-field
                v-model="form.user_email"
                :label="t('adminUserDetail.fields.email')"
                density="comfortable"
                variant="outlined"
                type="email"
                class="mb-3"
              />
          

              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.user_vp_name"
                    :label="t('adminUserDetail.fields.vpName')"
                    density="comfortable"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.user_vn_name"
                    :label="t('adminUserDetail.fields.vnName')"
                    density="comfortable"
                    variant="outlined"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="form.user_strasse"
                    :label="t('adminUserDetail.fields.street')"
                    density="comfortable"
                    variant="outlined"
                  />
                </v-col>
      
              </v-row>

              <v-row>
              
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="form.user_ort"
                    :label="t('adminUserDetail.fields.city')"
                    density="comfortable"
                    variant="outlined"
                  />
                </v-col>
              </v-row>

              <v-btn
                type="submit"
                color="#EA5545"
                variant="flat"
                :loading="isSaving"
                :disabled="isSaving"
                block
                class="mt-2"
              >
                {{ t('adminUserDetail.actions.saveChanges') }}
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-title class="card-title">
            <v-icon size="20" color="#EA5545" class="mr-2">mdi-package-variant</v-icon>
            {{ t('adminUserDetail.sections.products.title') }}
          </v-card-title>
          <v-card-subtitle class="card-subtitle">
            {{ t('adminUserDetail.sections.products.subtitle') }}
          </v-card-subtitle>
          <v-card-text>
            <div v-if="products.length === 0" class="state-block" data-state="empty">
              <v-icon color="#9ca3af" size="32" class="mb-2">mdi-package-variant-closed</v-icon>
              <p>{{ t('adminUserDetail.sections.products.empty') }}</p>
            </div>
            <div v-else class="product-table">
              <v-table density="comfortable">
                <thead>
                  <tr>
                    <th>{{ t('adminUserDetail.sections.products.table.product') }}</th>
                    <th>{{ t('adminUserDetail.sections.products.table.contract') }}</th>
                    <th>{{ t('adminUserDetail.sections.products.table.created') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in products" :key="product.myp_id">
                    <td>
                      <strong>{{ product.prod_name || ('#' + product.myp_product_id) }}</strong>
                      <div class="table-subtext">
                        ID {{ product.myp_product_id }}
                      </div>
                    </td>
                    <td>{{ product.myp_vertrags_nummer || '–' }}</td>
                    <td>{{ formatDate(product.myp_created_date) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <div class="add-product-card mt-6">
              <h4 class="add-product-title">
                {{ t('adminUserDetail.sections.products.addTitle') }}
              </h4>
              <p class="add-product-text">
                {{ t('adminUserDetail.sections.products.addSubtitle') }}
              </p>
              <v-select
                v-model="addProductForm.product_id"
                :items="productOptions"
                :label="t('adminUserDetail.sections.products.addSelect')"
                density="comfortable"
                variant="outlined"
                class="mb-3"
                :loading="isCatalogLoading"
                :disabled="isCatalogLoading"
              />
              <v-text-field
                v-model="addProductForm.contract_number"
                :label="t('adminUserDetail.sections.products.contractNumber')"
                density="comfortable"
                variant="outlined"
                class="mb-4"
              />
              <v-btn
                color="#EA5545"
                variant="flat"
                :disabled="!addProductForm.product_id || isAddingProduct"
                :loading="isAddingProduct"
                @click="addProductToUser"
                block
              >
                {{ t('adminUserDetail.sections.products.addButton') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="mb-6">
          <v-card-title class="card-title">
            <v-icon size="20" color="#EA5545" class="mr-2">mdi-shield-check</v-icon>
            {{ t('adminUserDetail.sections.status.title') }}
          </v-card-title>
          <v-card-subtitle class="card-subtitle">
            {{ t('adminUserDetail.sections.status.subtitle') }}
          </v-card-subtitle>
          <v-card-text>
            <div class="status-chip" :class="statusChipClass">
              <v-icon size="18" class="mr-2">
                {{ statusIcon }}
              </v-icon>
              {{ statusLabel }}
            </div>
            <p class="status-helper">
              {{ t('adminUserDetail.sections.status.helper') }}
            </p>
            <v-btn
              color="#EA5545"
              variant="flat"
              block
              class="mt-4"
              @click="activateAccount"
              :disabled="Number(form.user_status) === 10 || isSaving"
            >
              <v-icon left>mdi-flash</v-icon>
              {{ t('adminUserDetail.actions.activate') }}
            </v-btn>
            <v-btn
              color="#EA5545"
              variant="outlined"
              block
              class="mt-3"
              :disabled="!canSendPassword || isSendingPassword"
              :loading="isSendingPassword"
              @click="sendNewPassword"
            >
              <v-icon left>mdi-email-sync</v-icon>
              {{ t('adminUserDetail.actions.sendPassword') }}
            </v-btn>
            <p class="status-helper muted" v-if="!canSendPassword">
              {{ t('adminUserDetail.sections.status.passwordHintMissing') }}
            </p>
            <p class="status-helper" v-else>
              {{ t('adminUserDetail.sections.status.passwordHint') }}
            </p>
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-title class="card-title">
            <v-icon size="20" color="#EA5545" class="mr-2">mdi-information-outline</v-icon>
            {{ t('adminUserDetail.sections.meta.title') }}
          </v-card-title>
          <v-card-text>
            <div class="meta-row">
              <span>{{ t('adminUserDetail.sections.meta.userId') }}</span>
              <strong>#{{ detail?.user_id }}</strong>
            </div>
            <div class="meta-row">
              <span>{{ t('adminUserDetail.sections.meta.companyId') }}</span>
              <strong>#{{ detail?.user_company_id || '–' }}</strong>
            </div>
            <div class="meta-row">
              <span>{{ t('adminUserDetail.sections.meta.created') }}</span>
              <strong>{{ formatDate(detail?.user_created_date) }}</strong>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
        </v-row>
      </div>
    </v-card>
    </template>
  </div>
</template>

<script setup>
import { inject, ref, reactive, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const successStore = inject('successStore')
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const route = useRoute()
const router = useRouter()
const { t } = useLanguage()
const userStore = inject('userStore')
const fallbackUserRef = ref(null)
const { userData } = userStore ? storeToRefs(userStore) : { userData: fallbackUserRef }

const detail = ref(null)
const products = ref([])
const supports = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const isAddingProduct = ref(false)
const isCatalogLoading = ref(false)
const productCatalog = ref([])
const activeTab = ref('supports')
const isSendingPassword = ref(false)
const isAuthLoading = ref(true)
const needsLogin = ref(false)
const authError = ref('')

const isAuthorized = computed(() => {
  if (!userData.value) {
    return false
  }
  return Number(userData.value.user_admin || 0) === 1
})

const form = reactive({
  user_name: '',
  user_vorname: '',
  user_email: '',
  user_strasse: '',
  user_ort: '',
  user_status: 0,
  user_vp_name: '',
  user_vn_name: ''
})

const addProductForm = reactive({
  product_id: null,
  contract_number: ''
})

const userSummaryName = computed(() => {
  if (!detail.value) return ''
  return detail.value.user_vp_name || detail.value.user_name || ''
})

const userSummaryCompany = computed(() => {
  if (!detail.value) return ''
  const parts = [detail.value.com_name, detail.value.user_email].filter(Boolean)
  return parts.join(' · ')
})

const userSummaryAddress = computed(() => {
  if (!detail.value) return ''
  const parts = [detail.value.user_strasse, detail.value.user_ort].filter(Boolean)
  return parts.join(' · ')
})

const primaryContract = computed(() => {
  if (!products.value || products.value.length === 0) {
    return null
  }
  return products.value[0]
})

const primaryContractId = computed(() => {
  if (!primaryContract.value) return null
  return primaryContract.value.myp_id || primaryContract.value.myp_product_id || null
})

const primaryContractNumber = computed(() => {
  if (!primaryContract.value) return ''
  return primaryContract.value.myp_vertrags_nummer || ''
})

const canSendPassword = computed(() => {
  return !!(detail.value?.user_email && detail.value.user_email.length > 0)
})

const userId = computed(() => Number(route.params.id))

watch(userId, (newId, oldId) => {
  if (!newId || newId === oldId || !isAuthorized.value) {
    return
  }
  loadUserDetail()
})

watch(isAuthorized, (authorized) => {
  if (authorized) {
    loadProductCatalog()
    if (userId.value) {
      loadUserDetail()
    }
  } else if (!isAuthLoading.value) {
    resetDetailState()
  }
}, { immediate: true })

onMounted(() => {
  ensureAuth()
})

function goBack() {
  router.back()
}

function populateForm(user) {
  form.user_name = user?.user_name || ''
  form.user_email = user?.user_email || ''
  form.user_strasse = user?.user_strasse || ''
  form.user_ort = user?.user_ort || ''
  form.user_status = user?.user_status ?? 0
  form.user_vp_name = user?.user_vp_name || ''
  form.user_vn_name = user?.user_vn_name || ''
}

function resetDetailState() {
  detail.value = null
  products.value = []
  supports.value = []
  populateForm(null)
}

async function ensureAuth() {
  isAuthLoading.value = true
  needsLogin.value = false
  authError.value = ''
  try {
    if (!userData.value && baseCalls?.userGet) {
      const response = await baseCalls.userGet({ action: 'loggeduser', id: 0 })
      if (response?.data?.user) {
        userStore?.setUser?.(response.data.user)
      }
    }

    if (!userData.value) {
      needsLogin.value = true
      resetDetailState()
      return
    }

    if (!isAuthorized.value) {
      authError.value = t('adminUserDetail.auth.noAccessDescription')
      resetDetailState()
      return
    }
  } catch (error) {
    console.error('ensureAuth adminUserDetail', error)
    authError.value = t('adminUserDetail.auth.error')
  } finally {
    isAuthLoading.value = false
  }
}

async function loadUserDetail() {
  if (!userId.value || !isAuthorized.value) {
    return
  }
  isLoading.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'getcompanyuserdetail',
      user_id: userId.value
    })
    if (response.data?.success) {
      detail.value = response.data.user
      products.value = response.data.products || []
      supports.value = response.data.supports || []
      populateForm(response.data.user)
    } else {
      errorStore.setError(true, response.data?.message || t('adminUserDetail.messages.loadFailed'))
    }
  } catch (error) {
    console.error('loadUserDetail', error)
    errorStore.setError(true, t('adminUserDetail.messages.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

async function saveUserChanges() {
  if (!userId.value || !isAuthorized.value) {
    return
  }
  isSaving.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'adminupdateuser',
      user_id: userId.value,
      ...form
    })
    if (response.data?.success) {
      successStore.setSuccess(true, t('adminUserDetail.messages.saveSuccessTitle'), t('adminUserDetail.messages.saveSuccessBody'))
      await loadUserDetail()
    } else {
      errorStore.setError(true, response.data?.message || t('adminUserDetail.messages.saveFailed'))
    }
  } catch (error) {
    console.error('saveUserChanges', error)
    errorStore.setError(true, t('adminUserDetail.messages.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

async function loadProductCatalog() {
  if (!isAuthorized.value) {
    return
  }
  isCatalogLoading.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getproductcatalog'
    })
    if (response.data?.success) {
      productCatalog.value = response.data.products || []
    }
  } catch (error) {
    console.error('loadProductCatalog', error)
  } finally {
    isCatalogLoading.value = false
  }
}

async function addProductToUser() {
  if (!userId.value || !addProductForm.product_id || !isAuthorized.value) {
    return
  }
  isAddingProduct.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'assignproducttouser',
      user_id: userId.value,
      product_id: addProductForm.product_id,
      contract_number: addProductForm.contract_number
    })
    if (response.data?.success) {
      successStore.setSuccess(true, t('adminUserDetail.messages.productSuccessTitle'), t('adminUserDetail.messages.productSuccessBody'))
      addProductForm.product_id = null
      addProductForm.contract_number = ''
      await loadUserDetail()
    } else {
      errorStore.setError(true, response.data?.message || t('adminUserDetail.messages.productFailed'))
    }
  } catch (error) {
    console.error('addProductToUser', error)
    errorStore.setError(true, t('adminUserDetail.messages.productFailed'))
  } finally {
    isAddingProduct.value = false
  }
}

async function sendNewPassword() {
  if (!detail.value?.user_id || !canSendPassword.value || isSendingPassword.value || !isAuthorized.value) {
    return
  }
  isSendingPassword.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'sendcustomerpassword',
      user_id: detail.value.user_id
    })
    if (response.data?.success) {
      successStore?.setSuccess(
        true,
        t('adminUserDetail.notifications.passwordSentTitle'),
        t('adminUserDetail.notifications.passwordSentBody')
      )
    } else {
      errorStore?.setError?.(
        true,
        response.data?.message || t('adminUserDetail.notifications.passwordSendError')
      )
    }
  } catch (error) {
    console.error('sendNewPassword', error)
    errorStore?.setError?.(true, t('adminUserDetail.notifications.passwordSendError'))
  } finally {
    isSendingPassword.value = false
  }
}

function activateAccount() {
  form.user_status = 10
  saveUserChanges()
}

const statusLabel = computed(() => {
  if (Number(form.user_status) === 10) {
    return t('adminUserDetail.status.active')
  }
  return t('adminUserDetail.status.pending')
})

const statusChipClass = computed(() => ({
  'status-chip--active': Number(form.user_status) === 10,
  'status-chip--pending': Number(form.user_status) !== 10
}))

const statusIcon = computed(() =>
  Number(form.user_status) === 10 ? 'mdi-check-circle' : 'mdi-alert-circle'
)

const productOptions = computed(() =>
  productCatalog.value.map((product) => ({
    title: `${product.prod_name} (ID ${product.prod_id})`,
    value: product.prod_id
  }))
)

const supportStatusMap = {
  0: { label: 'adminIndex.status.open', color: '#FB8C00' },
  1: { label: 'adminIndex.status.done', color: '#4CAF50' },
  2: { label: 'adminIndex.status.closed', color: '#9E9E9E' },
  3: { label: 'adminIndex.status.urgent', color: '#E53935' }
}

function getSupportStatusLabel(status) {
  const mapEntry = supportStatusMap[Number(status)] || supportStatusMap[0]
  return t(mapEntry.label)
}

function getSupportStatusColor(status) {
  const mapEntry = supportStatusMap[Number(status)] || supportStatusMap[0]
  return mapEntry.color
}

function goToSupport(supportId) {
  router.push({ name: 'adminsupport', params: { id: supportId } })
}

function formatSupportDate(value) {
  if (!value) {
    return '–'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function supportProductLabel(support) {
  if (support.prod_name) {
    return support.prod_name
  }
  if (support.myp_vertrags_nummer) {
    return t('adminUserDetail.sections.supports.contractNumberLabel', {
      number: support.myp_vertrags_nummer
    })
  }
  if (support.sup_product_id) {
    return t('adminUserDetail.sections.supports.productIdLabel', {
      id: support.sup_product_id
    })
  }
  return ''
}

function truncateText(text, max = 200) {
  if (!text) {
    return ''
  }
  if (text.length <= max) {
    return text
  }
  return `${text.slice(0, max).trim()}...`
}

function formatDate(value) {
  if (!value || value === '1973-01-01 00:00:00') {
    return '–'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminUserDetail.css"></style>
